// views/Config/MetasGeracaoView.tsx
//
// Os dois lados do painel "Esperado x realizado" da aba Análise, em abas:
//
//   Laudo PVsyst   — a META: quanto cada usina DEVERIA gerar por mês.
//   Geração mensal — o REALIZADO: o que a coleta apurou, com correção manual
//                    quando a automação não fecha o mês.
//
// Ficam juntas de propósito. São a mesma pergunta vista dos dois lados, e quem
// abre para conferir um mês torto quase sempre precisa olhar os dois números.
//
// Existe porque a primeira carga (os cinco blocos de Morrinhos) entrou por SQL,
// e isso não escala: a cada laudo novo alguém teria que abrir o banco. Aqui a
// pessoa lê a tabela "Balanços e resultados principais" do PDF do PVsyst, digita
// os doze E_Grid e a tela faz o resto.
//
// ⚠️ Duas coisas que o formulário resolve e que erram fácil na mão:
//
//   1. O laudo dá a energia da USINA INTEIRA; a meta é por bloco. O rateio é
//      por potência (kWp do bloco / kWp da usina) — válido porque os grupos do
//      relatório são idênticos em módulo, inclinação e inversor, mudando só a
//      quantidade. Digitar bloco a bloco convidaria ao erro de conta.
//   2. A meta é gravada em kWh ABSOLUTOS, não em kWh/kWp. A potência do cadastro
//      (unidades_geradoras.capacidade_instalada) e a Pnom DC do laudo divergem —
//      em Morrinhos, 4.950 contra 6.121 kWp — e uma meta normalizada herdaria
//      essa ambiguidade, errando 24% para cima. Ver metaNoPeriodo.
//
// A meta não tem ano: a chave é (usina, mês) e o laudo é de ano meteorológico
// típico, então vale para 2026, 2027 e seguintes sem recadastro.
import React, { useEffect, useMemo, useState } from 'react';
import {
    Target, Loader2, Save, Trash2, AlertTriangle, CheckCircle2,
    FileText, Calculator, Info, CalendarRange,
} from 'lucide-react';
import { api } from '../../src/services/api';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { logUsageEvent } from '../../src/lib/logger';
import type { UnidadeGeradora, MetaGeradoraRow } from '../../types';
import GradeGeracaoAnual from './GradeGeracaoAnual';
import { getDisplayUsina } from '../Geradoras/geradorasShared';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/** Estado do formulário do laudo. Tudo string: campo numérico vazio não é 0. */
interface FormLaudo {
    /** Pnom DC da usina inteira, como está no laudo (kWp). */
    pnomUsina: string;
    /** Pnom DC do bloco que está sendo cadastrado (kWp). */
    pnomBloco: string;
    /** E_Grid de cada mês, em MWh, na ordem jan..dez. */
    mensal: string[];
    /** P50 e P90 anuais em MWh — só para derivar a razão do piso. */
    p50Ano: string;
    p90Ano: string;
    fonte: string;
}

const FORM_VAZIO: FormLaudo = {
    pnomUsina: '', pnomBloco: '', mensal: Array(12).fill(''), p50Ano: '', p90Ano: '', fonte: '',
};

const num = (v: string): number | null => {
    const n = Number(String(v).replace(/\./g, '').replace(',', '.').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
};

const fmt = (v: number | null): string =>
    v == null ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });

const MetasGeracaoView: React.FC = () => {
    const { user } = useAuth();
    const { addToast } = useToast();

    const [unidades, setUnidades] = useState<UnidadeGeradora[]>([]);
    const [metas, setMetas] = useState<MetaGeradoraRow[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const [aba, setAba] = useState<'laudo' | 'geracao'>('laudo');
    const [site, setSite] = useState<string>('');
    const [form, setForm] = useState<FormLaudo>(FORM_VAZIO);
    /** Blocos que recebem a MESMA tabela mensal, rateada pela potência de cada um. */
    const [replicar, setReplicar] = useState<Record<string, string>>({});

    useEffect(() => {
        logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'metas-geracao', acao: null, origem: 'navegacao' });
    }, [user?.id]);

    const carregar = async () => {
        setCarregando(true);
        try {
            const [u, m] = await Promise.all([
                api.geradoras.getUnidades(),
                api.geradoras.getMetas().catch(() => [] as MetaGeradoraRow[]),
            ]);
            setUnidades(u);
            setMetas(m);
            setSite(s => s || u[0]?.site_id_api || '');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao carregar as usinas.', 'error');
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const nomeDe = (u: UnidadeGeradora) => {
        const d = getDisplayUsina(u);
        return `${d.marca} · ${d.uc}`;
    };

    const metasPorSite = useMemo(() => {
        const m: Record<string, MetaGeradoraRow[]> = {};
        metas.forEach(x => { (m[x.site_id_api] ||= []).push(x); });
        return m;
    }, [metas]);

    const metasDoSite = metasPorSite[site] || [];
    const selecionada = unidades.find(u => u.site_id_api === site);

    // Ao trocar de usina, recarrega o formulário com o que já está gravado —
    // corrigir um laudo não pode exigir redigitar os doze meses.
    useEffect(() => {
        const linhas = metasPorSite[site] || [];
        if (linhas.length === 0) { setForm(FORM_VAZIO); setReplicar({}); return; }

        const porMes = new Map(linhas.map(l => [l.mes, l]));
        const pnomBloco = linhas[0].pnom_kwp_ref;
        const pnomUsina = linhas[0].pnom_kwp_usina;

        // E_Grid da usina = kwh_por_kwp * Pnom da usina. Precisa das DUAS
        // potências gravadas: kwh_esperado sozinho não serve, porque
        // kwh_esperado = kwh_por_kwp * pnom_kwp_ref e a Pnom da usina se cancela.
        const mensal = Array.from({ length: 12 }, (_, i) => {
            const l = porMes.get(i + 1);
            if (!l?.kwh_por_kwp || !pnomUsina) return '';
            return String(Math.round(l.kwh_por_kwp * pnomUsina / 1000));
        });

        // P50/P90 anuais: o laudo não é regravado, mas a RAZÃO entre eles está
        // implícita nas linhas. Reconstruir os dois mantém o piso ao reeditar,
        // em vez de perdê-lo silenciosamente no próximo save.
        const somaP50 = linhas.reduce((a, l) => a + (l.kwh_esperado ?? 0), 0);
        const somaP90 = linhas.reduce((a, l) => a + (l.kwh_esperado_p90 ?? 0), 0);
        const p50AnoUsina = pnomBloco && pnomUsina ? somaP50 * pnomUsina / pnomBloco / 1000 : 0;
        const razao = somaP50 > 0 ? somaP90 / somaP50 : 0;

        setForm({
            pnomUsina: pnomUsina ? String(pnomUsina) : '',
            pnomBloco: pnomBloco ? String(pnomBloco) : '',
            mensal,
            p50Ano: p50AnoUsina > 0 ? String(Math.round(p50AnoUsina)) : '',
            p90Ano: p50AnoUsina > 0 && razao > 0 ? String(Math.round(p50AnoUsina * razao)) : '',
            fonte: linhas[0].fonte || '',
        });
        setReplicar({});
    }, [site, metasPorSite]);

    // ---- cálculo do que será gravado ----
    const previa = useMemo(() => {
        const pnomUsina = num(form.pnomUsina);
        const pnomBloco = num(form.pnomBloco);
        if (!pnomUsina || !pnomBloco) return null;

        const p50 = num(form.p50Ano);
        const p90 = num(form.p90Ano);
        // Sem P50/P90 anuais o piso não é inventado: fica null e a tela some
        // com a linha "mínimo provável" em vez de desenhar um número falso.
        const razaoP90 = p50 && p90 ? p90 / p50 : null;

        const linhas = form.mensal.map((v, i) => {
            const mwh = num(v);
            if (!mwh) return null;
            const kwhUsina = mwh * 1000;
            return {
                mes: i + 1,
                kwh_por_kwp: Math.round((kwhUsina / pnomUsina) * 100) / 100,
                kwh_esperado: Math.round(kwhUsina * pnomBloco / pnomUsina),
                kwh_esperado_p90: razaoP90 ? Math.round(kwhUsina * pnomBloco / pnomUsina * razaoP90) : null,
            };
        });

        const preenchidos = linhas.filter(Boolean) as NonNullable<typeof linhas[number]>[];
        return {
            linhas: preenchidos,
            faltando: 12 - preenchidos.length,
            totalAno: preenchidos.reduce((s, l) => s + l.kwh_esperado, 0),
            kwhKwpAno: preenchidos.reduce((s, l) => s + l.kwh_por_kwp, 0),
            razaoP90,
        };
    }, [form]);

    const blocosParaReplicar = useMemo(
        () => unidades.filter(u => u.site_id_api !== site),
        [unidades, site],
    );

    const salvar = async () => {
        if (!previa || previa.linhas.length === 0) {
            addToast('Preencha a potência e ao menos um mês.', 'error');
            return;
        }
        if (previa.faltando > 0 &&
            !confirm(`${previa.faltando} mês(es) sem valor não serão gravados. A tela de Análise mostrará "sem laudo" neles. Continuar?`)) {
            return;
        }

        const pnomUsina = num(form.pnomUsina)!;
        const alvos: { site: string; pnom: number }[] = [
            { site, pnom: num(form.pnomBloco)! },
            ...Object.entries(replicar)
                .map(([s, kwp]) => ({ site: s, pnom: num(kwp) }))
                .filter((x): x is { site: string; pnom: number } => x.pnom != null),
        ];

        const rows = alvos.flatMap(alvo =>
            form.mensal.map((v, i) => {
                const mwh = num(v);
                if (!mwh) return null;
                const kwhUsina = mwh * 1000;
                return {
                    site_id_api: alvo.site,
                    mes: i + 1,
                    kwh_por_kwp: Math.round((kwhUsina / pnomUsina) * 100) / 100,
                    kwh_esperado: Math.round(kwhUsina * alvo.pnom / pnomUsina),
                    kwh_esperado_p90: previa.razaoP90
                        ? Math.round(kwhUsina * alvo.pnom / pnomUsina * previa.razaoP90)
                        : null,
                    pnom_kwp_ref: alvo.pnom,
                    pnom_kwp_usina: pnomUsina,
                    fonte: form.fonte.trim() || null,
                    observacao: `Rateio por potencia: ${alvo.pnom} de ${pnomUsina} kWp da usina. E_Grid do mes no laudo: ${mwh} MWh.`,
                };
            }).filter(Boolean),
        ) as Partial<MetaGeradoraRow>[];

        setSalvando(true);
        try {
            await api.geradoras.salvarMetas(rows);
            addToast(`Meta gravada para ${alvos.length} bloco(s), ${previa.linhas.length} meses.`, 'success');
            await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao gravar a meta.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    const apagar = async () => {
        if (!confirm(`Apagar a meta de ${selecionada ? nomeDe(selecionada) : site}? O painel de Análise volta a mostrar "sem laudo" para este bloco.`)) return;
        setSalvando(true);
        try {
            await api.geradoras.apagarMetas(site);
            addToast('Meta apagada.', 'success');
            await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao apagar.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    if (carregando) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
                <Loader2 size={20} className="animate-spin mr-2" /> Carregando usinas...
            </div>
        );
    }

    return (
        <div className="max-w-5xl space-y-6 pb-12">
            <div>
                <h1 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Target size={22} className="text-primary" />
                    Geração e metas
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    A meta de cada usina e a geração apurada, mês a mês. Alimentam o painel
                    <b> Esperado x realizado</b> em Geradoras &gt; Análise.
                </p>
            </div>

            <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700">
                {([
                    ['laudo', 'Laudo PVsyst', <Target size={14} key="a" />, 'A meta: quanto deveria gerar'],
                    ['geracao', 'Geração mensal', <CalendarRange size={14} key="b" />, 'O realizado, com ajuste manual'],
                ] as const).map(([id, rot, icone, dica]) => (
                    <button
                        key={id}
                        onClick={() => setAba(id)}
                        title={dica}
                        aria-current={aba === id ? 'page' : undefined}
                        className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors -mb-px border-b-2 ${aba === id
                            ? 'text-primary border-primary'
                            : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200'}`}
                    >
                        {icone}{rot}
                    </button>
                ))}
            </div>

            {aba === 'geracao' ? (
                <GradeGeracaoAnual unidades={unidades} site={site} onTrocarSite={setSite} />
            ) : (
              <>
            <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/15 px-4 py-3 flex items-start gap-2">
                <Info size={15} className="text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                    <p>
                        <b>A meta não tem ano.</b> O laudo é de ano meteorológico típico, então os mesmos
                        doze valores valem para 2026, 2027 e seguintes — não precisa recadastrar na virada.
                    </p>
                    <p>
                        Os números saem da tabela <b>&quot;Balanços e resultados principais&quot;</b> do PDF,
                        coluna <b>E_Grid</b> (energia injetada na rede), em MWh.
                    </p>
                </div>
            </div>

            {/* Usina */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1.5">
                    Bloco / usina
                </label>
                <select
                    value={site}
                    onChange={e => setSite(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                    {unidades.map(u => (
                        <option key={u.site_id_api} value={u.site_id_api}>
                            {nomeDe(u)}
                            {(metasPorSite[u.site_id_api]?.length ?? 0) > 0
                                ? `  ✓ ${metasPorSite[u.site_id_api].length} meses`
                                : '  — sem meta'}
                        </option>
                    ))}
                </select>
                {selecionada && (
                    <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                        Capacidade no cadastro: <b>{selecionada.capacidade_instalada} MWp</b>. Não é
                        necessariamente a Pnom DC do laudo — por isso a potência abaixo é digitada à parte.
                    </p>
                )}
                {metasDoSite.length > 0 && metasDoSite[0].fonte && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={12} /> Já cadastrado: {metasDoSite[0].fonte}
                    </p>
                )}
            </div>

            {/* Potências e proveniência */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 space-y-4">
                <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-sm flex items-center gap-2">
                    <Calculator size={15} className="text-primary" /> Dados do laudo
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                        { k: 'pnomUsina' as const, rot: 'Pnom DC da usina inteira (kWp)', dica: 'No PVsyst: "Pnom total" do grupo FV. Morrinhos: 6121.' },
                        { k: 'pnomBloco' as const, rot: 'Pnom DC deste bloco (kWp)', dica: 'A potência só deste bloco. Morrinhos: 1360 nos blocos 1-4, 680 no 5.' },
                        { k: 'p50Ano' as const, rot: 'P50 anual (MWh) — opcional', dica: 'Energia produzida no ano. Usado só para calcular o piso.' },
                        { k: 'p90Ano' as const, rot: 'P90 anual (MWh) — opcional', dica: 'Da seção "Avaliação P50 - P90". Sem ele, a tela não mostra piso.' },
                    ].map(c => (
                        <div key={c.k}>
                            <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
                                {c.rot}
                            </label>
                            <input
                                type="text" inputMode="decimal"
                                value={form[c.k]}
                                onChange={e => setForm(f => ({ ...f, [c.k]: e.target.value }))}
                                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white tabular-nums focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">{c.dica}</p>
                        </div>
                    ))}
                </div>

                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
                        Origem (aparece na tela de Análise)
                    </label>
                    <input
                        type="text"
                        value={form.fonte}
                        onChange={e => setForm(f => ({ ...f, fonte: e.target.value }))}
                        placeholder="PVsyst 7.2.4 - Nome_do_Relatorio R02 - simulacao 10/11/2023"
                        className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                </div>
            </div>

            {/* Doze meses */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-sm flex items-center gap-2 mb-1">
                    <FileText size={15} className="text-primary" /> E_Grid mês a mês (MWh)
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
                    Energia da <b>usina inteira</b>, como está no laudo. O rateio para este bloco é automático.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {MESES.map((nome, i) => (
                        <div key={nome}>
                            <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
                                {nome}
                            </label>
                            <input
                                type="text" inputMode="decimal"
                                value={form.mensal[i]}
                                onChange={e => setForm(f => {
                                    const mensal = [...f.mensal];
                                    mensal[i] = e.target.value;
                                    return { ...f, mensal };
                                })}
                                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white tabular-nums focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Replicar para os blocos irmãos */}
            {blocosParaReplicar.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                    <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-sm mb-1">
                        Aplicar o mesmo laudo a outros blocos
                    </h2>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                        Uma usina costuma ter vários blocos no mesmo relatório. Marque os irmãos e informe
                        a Pnom DC de cada um — a energia é rateada por potência. Deixe em branco para pular.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {blocosParaReplicar.map(u => (
                            <div key={u.site_id_api} className="flex items-center gap-2">
                                <input
                                    type="text" inputMode="decimal"
                                    placeholder="kWp"
                                    value={replicar[u.site_id_api] ?? ''}
                                    onChange={e => setReplicar(r => ({ ...r, [u.site_id_api]: e.target.value }))}
                                    className="w-24 px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white tabular-nums shrink-0"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{nomeDe(u)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Prévia */}
            {previa && previa.linhas.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                        <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-sm">
                            O que vai ser gravado neste bloco
                        </h2>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
                            {previa.linhas.length} meses · {fmt(previa.totalAno)} kWh no ano ·
                            {' '}{previa.kwhKwpAno.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh/kWp
                            {previa.razaoP90 == null && ' · sem piso P90 (P50/P90 anuais em branco)'}
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[26rem]">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 px-4 font-bold">Mês</th>
                                    <th className="py-2 px-4 font-bold text-right">Esperado (kWh)</th>
                                    <th className="py-2 px-4 font-bold text-right">Mínimo P90</th>
                                    <th className="py-2 px-4 font-bold text-right">kWh/kWp</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                                {previa.linhas.map(l => (
                                    <tr key={l.mes}>
                                        <td className="py-1.5 px-4 font-semibold text-gray-800 dark:text-gray-200">{MESES[l.mes - 1]}</td>
                                        <td className="py-1.5 px-4 text-right tabular-nums text-gray-900 dark:text-white font-semibold">{fmt(l.kwh_esperado)}</td>
                                        <td className="py-1.5 px-4 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmt(l.kwh_esperado_p90)}</td>
                                        <td className="py-1.5 px-4 text-right tabular-nums text-gray-500 dark:text-gray-400">
                                            {l.kwh_por_kwp.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {previa.faltando > 0 && (
                        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-start gap-2">
                            <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-[11px] text-gray-600 dark:text-gray-400">
                                {previa.faltando} mês(es) em branco não serão gravados — a Análise mostrará
                                &quot;sem laudo&quot; neles em vez de inventar um número.
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    onClick={salvar}
                    disabled={salvando || !previa || previa.linhas.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    {salvando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    Salvar meta
                </button>
                {metasDoSite.length > 0 && (
                    <button
                        onClick={apagar}
                        disabled={salvando}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-sm font-bold hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40 transition-colors"
                    >
                        <Trash2 size={15} /> Apagar deste bloco
                    </button>
                )}
            </div>

              </>
            )}

            {/* Panorama: vale para as duas abas — e o mapa de quem ja tem laudo. */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-sm mb-3">
                    Cobertura das metas
                </h2>
                <div className="space-y-1.5">
                    {unidades.map(u => {
                        const n = metasPorSite[u.site_id_api]?.length ?? 0;
                        return (
                            <div key={u.site_id_api} className="flex items-center justify-between gap-3 text-xs py-1">
                                <span className="text-gray-700 dark:text-gray-300 truncate">{nomeDe(u)}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap shrink-0 ${n === 12
                                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                                    : n > 0
                                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600'}`}>
                                    {n === 12 ? '12 meses' : n > 0 ? `${n} de 12 meses` : 'sem meta'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MetasGeracaoView;
